import React, { useEffect, useState } from "react";
import clsx from "clsx";
import styles from "./HeroSection.module.css";
import { Container } from "../../common/Container";
import { AppStoreBadge } from "./AppStoreBadge";
import { heroData, howItWorksData, screenshots } from "../../../data/compassData";

const SLIDE_MS = 4000;

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

const PauseIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <rect x="6.5" y="5" width="4" height="14" rx="1.5" />
    <rect x="13.5" y="5" width="4" height="14" rx="1.5" />
  </svg>
);

const PlayIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M8 5.6v12.8a1 1 0 001.52.85l10.2-6.4a1 1 0 000-1.7L9.52 4.75A1 1 0 008 5.6z" />
  </svg>
);

export const HeroSection: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  // Bumped on every tab click so re-clicking the active tab also restarts the timer.
  const [clickCount, setClickCount] = useState(0);
  // Reduced-motion users start paused; everyone can pause or resume.
  const [isPaused, setIsPaused] = useState(prefersReducedMotion);
  // Hover, keyboard focus, or a hidden browser tab hold the current slide.
  const [isHeld, setIsHeld] = useState(false);
  const [isPageHidden, setIsPageHidden] = useState(false);

  useEffect(() => {
    const onVisibility = () => setIsPageHidden(document.hidden);
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  const isPlaying = !isPaused && !isHeld && !isPageHidden;

  // Restart the full countdown whenever the slide changes, a tab is clicked,
  // or playback resumes.
  useEffect(() => {
    if (!isPlaying) return;
    const timeout = setTimeout(() => {
      setActiveIndex((prev) => (prev + 1) % screenshots.length);
    }, SLIDE_MS);
    return () => clearTimeout(timeout);
  }, [activeIndex, clickCount, isPlaying]);

  const selectSlide = (index: number) => {
    setActiveIndex(index);
    setClickCount((count) => count + 1);
  };

  return (
    <section className={styles.hero} aria-labelledby="compass-hero-title">
      <Container>
        <div className={styles.content}>
          <div className={styles.textContent}>
            <h1 id="compass-hero-title" className={styles.headline}>
              {heroData.headline}
            </h1>
            <p className={styles.subheadline}>{heroData.subheadline}</p>

            {/* The idea in one glance: a value, the direction it takes, today's habit */}
            <ol
              className={styles.chain}
              aria-label={`For example: ${howItWorksData.map((step) => step.example).join(", then ")}`}
            >
              {howItWorksData.map((step) => (
                <li key={step.step} className={styles.chainItem} data-kind={step.kind}>
                  <span className={styles.chainKind}>{step.kind}</span>
                  <span className={styles.chainExample}>{step.example}</span>
                </li>
              ))}
            </ol>

            <div className={styles.ctas}>
              <AppStoreBadge />
              <a href="#how-it-works" className={styles.secondaryCta}>
                {heroData.ctaSecondary}
                <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path
                    fillRule="evenodd"
                    d="M10 3a.75.75 0 01.75.75v10.638l3.96-4.158a.75.75 0 111.08 1.04l-5.25 5.5a.75.75 0 01-1.08 0l-5.25-5.5a.75.75 0 111.08-1.04l3.96 4.158V3.75A.75.75 0 0110 3z"
                    clipRule="evenodd"
                  />
                </svg>
              </a>
            </div>
            <p className={styles.reassurance}>{heroData.reassurance}</p>
          </div>

          <div
            className={styles.showcase}
            onMouseEnter={() => setIsHeld(true)}
            onMouseLeave={() => setIsHeld(false)}
            onFocus={() => setIsHeld(true)}
            onBlur={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                setIsHeld(false);
              }
            }}
          >
            <div className={styles.phoneFrame}>
              <div className={styles.phoneScreen}>
                {screenshots.map((screenshot, index) => (
                  <img
                    key={screenshot.src}
                    src={screenshot.src}
                    alt={index === activeIndex ? screenshot.alt : ""}
                    aria-hidden={index !== activeIndex}
                    width={828}
                    height={1800}
                    loading={index === 0 ? "eager" : "lazy"}
                    className={clsx(
                      styles.screenshot,
                      index === activeIndex && styles.screenshotActive,
                    )}
                  />
                ))}
              </div>
            </div>

            <div className={styles.controls}>
              <div className={styles.segmented} role="group" aria-label="App screens">
                {screenshots.map((screenshot, index) => (
                  <button
                    key={screenshot.label}
                    type="button"
                    className={clsx(styles.segment, index === activeIndex && styles.segmentActive)}
                    aria-pressed={index === activeIndex}
                    onClick={() => selectSlide(index)}
                  >
                    {screenshot.label}
                  </button>
                ))}
              </div>
              <button
                type="button"
                className={styles.playToggle}
                onClick={() => setIsPaused((paused) => !paused)}
                aria-label={isPaused ? "Play slideshow" : "Pause slideshow"}
              >
                {isPaused ? <PlayIcon /> : <PauseIcon />}
              </button>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
};
